import Papa from "papaparse";
import { IUserPermissions } from "src/types/generic";

export const formatBytes = (bytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))));
  return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i];
};

// Function to get first private route that user have access to
export const getFirstPrivateRoute = (
  permissions: IUserPermissions,
): string | undefined => {
  if (permissions?.reports.viewSalesSummary) {
    return "reports/sales";
  }
  if (permissions?.businessIntelligence.viewTransactions) {
    return "business-intelligence/trans";
  }
  if (permissions?.locations.view) {
    return "locations/active";
  }
  if (permissions?.transactions?.view) {
    return "transactions/upcoming";
  }
  if (permissions?.transactions?.ticketValidation) {
    return "transactions/tickets-validation";
  }
  if (permissions?.transactions?.golfRequests) {
    return "transactions/golf-requests";
  }
  if (permissions?.customers?.viewFeedback) {
    return "customers/feedback";
  }
  if (permissions?.customers?.viewCustomersList) {
    return "customers/list";
  }
  if (permissions?.arsannEmployees?.view) {
    return "employees/active";
  }
  if (permissions?.delivery?.view) {
    return "delivery/active";
  }

  // If none of the conditions are met, return undefined
  return undefined;
};

export const formatNumber = (input: number) => {
  // Convert input to a number
  const num = Number(input);

  // If it's not a valid number, return the input as is
  if (isNaN(num)) {
    return input;
  }

  // Otherwise, format the number with commas
  return new Intl.NumberFormat().format(num);
};

export const downloadFile = (file: any, type: string, name: string) => {
  // Convert file data to Blob
  const blob = new Blob([file], { type: `${type};charset=utf-8;` });
  // Create a link element
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = name;
  // Append the link to the body and trigger the download
  document.body.appendChild(link);
  link.click();
  // Clean up
  document.body.removeChild(link);
};

export function getFirstAndLastLetters(sentence: string) {
  if (!sentence) {
    return "";
  }
  // Trim spaces and split the sentence into words
  let words = sentence.trim().split(/\s+/);

  // Get the first letter of the first word and the first letter of the last word
  let firstLetter = words[0][0];
  let lastLetter = words.length > 1 ? words[words.length - 1][0] : "";

  return firstLetter + lastLetter; // You can return them as a string or array as per need
}

export type QueryParamValue = string | number | (string | number)[];

export function toQueryString(params: Record<string, QueryParamValue>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item))); // Convert array items to strings
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, String(value)); // Convert non-array values to string
    }
  });

  return searchParams.toString();
}

export const isMediaUrlVideo = (url: string): boolean => {
  const videoExtensions = [
    ".mp4",
    ".mov",
    ".avi",
    ".wmv",
    ".flv",
    ".mkv",
    ".webm",
  ];

  // Get the part before any query parameters
  const cleanUrl = url.split("?")[0].toLowerCase();

  return videoExtensions.some((ext) => cleanUrl.endsWith(ext));
};

export const downloadCsvFile = (
  columns: string[], // Column headers
  data: Record<string, any>[], // Array of objects or arrays representing rows
  filename = "data.csv",
) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    console.error("Columns must be a non-empty array");
    return;
  }

  if (!Array.isArray(data) || data.length === 0) {
    console.error("Data must be a non-empty array");
    return;
  }

  // Convert data to CSV format
  const csvContent = Papa.unparse({
    fields: columns, // The header row
    data: data.map((row) => columns.map((col) => row[col] ?? "")), // Map to match column order
  });

  // Ensure proper UTF-8 encoding with BOM for Excel compatibility
  const utf8CsvContent = `\uFEFF${csvContent}`;

  // Create a downloadable blob
  const blob = new Blob([utf8CsvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  // Create a temporary link and trigger download
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const chunkArray = (array: string[], size: number) => {
  const result: string[][] = [];

  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }

  return result;
};

export function hoursDecimalToHM(decimal: number): string {
  if (decimal >= 1) {
    return `${decimal.toFixed(1)}hrs.`;
  }

  const minutes = Math.round(decimal * 60);
  return `${minutes}mins.`;
}

export const formatCompactNumber = (value: number) => {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return value.toString();
};
